// Stage B — Bestelling detailweergave met operationele en conversie-acties.
// RBAC:
//  - Medewerker: bekijken, markeren klaargezet/uitgevoerd, notitie, pakbon
//    aanmaken, label aanmaken, gekoppelde documenten openen.
//  - Manager / Admin / Owner / Webshop Manager: ook factuur, waardeverklaring
//    en boekingsformulier aanmaken, dubbele documenten forceren, bevestigings-
//    mail handmatig opnieuw versturen.
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, FileText, Package, Tag, ScrollText,
  Receipt, ClipboardList, Truck, Mail, AlertTriangle, MessageSquare,
  PlayCircle, Lock, ExternalLink, Archive, RotateCcw, EyeOff,
} from "lucide-react";

const PAYMENT_LABELS = {
  paid: "Betaald", unpaid: "Onbetaald", pending: "In afwachting",
  refunded: "Terugbetaald", failed: "Mislukt",
};

// Documenttype catalog: type → { label, perm, icon }
const DOC_TYPES = {
  packing_slip:      { label: "Pakbon",            icon: Package,        adminUrl: (id) => `/admin?section=documenten&pakbon=${id}` },
  shipping_label:    { label: "Label",             icon: Tag,            adminUrl: (id) => `/admin?section=tracking_labels&label=${id}` },
  invoice:           { label: "Factuur",           icon: Receipt,        adminUrl: (id) => `/admin?section=facturen&invoice=${id}` },
  value_declaration: { label: "Waardeverklaring",  icon: ScrollText,     adminUrl: (id) => `/admin?section=facturen&generated=${id}` },
  booking_form:      { label: "Boekingsformulier", icon: ClipboardList,  adminUrl: (id) => `/admin?section=documenten&boeking=${id}` },
};

const CONVERSIONS = [
  { type: "packing_slip",      label: "Pakbon",            endpoint: "convert-to-packing-slip",      perm: "orders:convert_operational" },
  { type: "shipping_label",    label: "Label",             endpoint: "convert-to-label",             perm: "orders:convert_operational" },
  { type: "invoice",           label: "Factuur",           endpoint: "convert-to-invoice",           perm: "orders:convert_financial" },
  { type: "value_declaration", label: "Waardeverklaring",  endpoint: "convert-to-value-declaration", perm: "orders:convert_financial" },
  { type: "booking_form",      label: "Boekingsformulier", endpoint: "convert-to-booking-form",      perm: "orders:convert_financial" },
];

export default function BestellingDetail({ orderId, onBack }) {
  const { hasPermission } = useAuth();
  const [order, setOrder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [dupDialog, setDupDialog] = useState(null); // {type, existing, label}
  const [archiveDialog, setArchiveDialog] = useState(null); // {doc, reason}
  const [showArchived, setShowArchived] = useState(false);

  const canOperate = hasPermission("orders:operational");
  const canConvertOp = hasPermission("orders:convert_operational");
  const canConvertFin = hasPermission("orders:convert_financial");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        adminApi.get(`/admin/webshop/orders/${orderId}`),
        adminApi.get(`/admin/orders/${orderId}/documents`, {
          params: { include_archived: showArchived },
        }),
      ]);
      setOrder(r1.data);
      setDocs(r2.data?.documents || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [orderId, showArchived]);

  useEffect(() => { load(); }, [load]);

  const callAction = async (path, body) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/orders/${orderId}/${path}`, body || {});
      return r.data;
    } finally {
      setBusy(false);
    }
  };

  const onMarkPrepared = async () => {
    try {
      const r = await callAction("mark-prepared");
      if (r?.already_prepared) toast.info("Bestelling stond al gemarkeerd als klaargezet.");
      else toast.success("Bestelling gemarkeerd als klaargezet.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onMarkExecuted = async () => {
    try {
      const r = await callAction("mark-executed");
      if (r?.already_executed) toast.info("Bestelling stond al gemarkeerd als uitgevoerd.");
      else toast.success("Bestelling gemarkeerd als uitgevoerd.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onAddNote = async () => {
    const text = noteText.trim();
    if (!text) {
      toast.error("Notitie mag niet leeg zijn.");
      return;
    }
    try {
      await callAction("operational-note", { note: text });
      toast.success("Operationele notitie toegevoegd.");
      setNoteOpen(false);
      setNoteText("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onConvert = async (conv, opts = { force: false }) => {
    if (!hasPermission(conv.perm)) {
      toast.error(`Geen permissie voor ${conv.label}.`);
      return;
    }
    try {
      const r = await callAction(conv.endpoint, { force: !!opts.force });
      if (r?.duplicate && !opts.force) {
        setDupDialog({ ...conv, existing: r.existing_document });
        return;
      }
      if (r?.success) {
        toast.success(`${conv.label} aangemaakt${r.document_number ? ` · ${r.document_number}` : ""}.`);
        await load();
      } else {
        toast.error("Onverwachte respons bij conversie.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onResendShopEmail = async () => {
    try {
      const r = await callAction("send-confirmation-email");
      if (r?.status === "sent") toast.success("Bevestigingsmail verstuurd naar shop@5999Cargo.nl.");
      else toast.warning(`E-mail status: ${r?.status || "onbekend"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onArchiveDocument = async (doc, reason) => {
    setBusy(true);
    try {
      await adminApi.post(
        `/admin/orders/${orderId}/documents/${doc.id}/archive`,
        { reason: (reason || "").trim() || null },
      );
      toast.success(`Document gearchiveerd${doc.number ? ` (${doc.number})` : ""}.`);
      setArchiveDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onUnarchiveDocument = async (doc) => {
    setBusy(true);
    try {
      await adminApi.post(
        `/admin/orders/${orderId}/documents/${doc.id}/unarchive`,
      );
      toast.success("Document teruggezet als actief.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={onBack} className="rounded-none text-xs" data-testid="bestelling-detail-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Terug naar bestellingen
        </Button>
        <div className="p-10 text-gray-500">Bestelling laden…</div>
      </div>
    );
  }

  // P0 hotfix — effectief betaald = status='paid' OR payment_status='paid'.
  // Legacy orders hebben soms status='paid' zonder payment_status sync, dus we
  // accepteren beide als bewijs van een afgeronde betaling.
  const paid = order.payment_status === "paid" || order.status === "paid";
  const cust = order.customer || {};
  const ship = order.shipping_address || {};
  const items = order.items || [];

  return (
    <div data-testid="bestelling-detail" className="space-y-6">
      {/* Header / breadcrumb */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <Button variant="outline" onClick={onBack} className="rounded-none text-xs mb-3" data-testid="bestelling-detail-back">
            <ArrowLeft className="w-3 h-3 mr-1" /> Terug
          </Button>
          <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">
            Bestelling {order.order_number || order.id?.slice(0, 8)}
          </h1>
          <div className="text-xs text-gray-500 font-mono mt-1">{order.id}</div>
        </div>
        <div className="text-right space-y-1">
          <div className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Totaal</div>
          <div className="font-chivo font-black text-3xl text-[#0A192F]">
            € {Number(order.amount_total || 0).toFixed(2)}
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span
              className={`inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide ${
                paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"
              }`}
              data-testid="bestelling-payment-status"
            >
              {PAYMENT_LABELS[order.payment_status] || order.payment_status}
            </span>
            {order.executed_at && (
              <span className="inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide bg-blue-100 text-blue-700" data-testid="bestelling-op-executed">
                Uitgevoerd
              </span>
            )}
            {!order.executed_at && order.prepared_at && (
              <span className="inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800" data-testid="bestelling-op-prepared">
                Klaargezet
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Operationele acties */}
      <div className="bg-white border border-gray-200 p-5">
        <h2 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-3">
          Operationele acties
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onMarkPrepared}
            disabled={!canOperate || busy || !paid || !!order.prepared_at}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            data-testid="action-mark-prepared"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {order.prepared_at ? "Klaargezet ✓" : "Markeer klaargezet"}
          </Button>
          <Button
            onClick={onMarkExecuted}
            disabled={!canOperate || busy || !paid || !!order.executed_at}
            className="rounded-none bg-blue-700 hover:bg-blue-800 text-white text-xs"
            data-testid="action-mark-executed"
          >
            <PlayCircle className="w-3 h-3 mr-1" />
            {order.executed_at ? "Uitgevoerd ✓" : "Markeer uitgevoerd"}
          </Button>
          <Button
            onClick={() => setNoteOpen(true)}
            disabled={!canOperate || busy}
            variant="outline"
            className="rounded-none text-xs"
            data-testid="action-add-note"
          >
            <MessageSquare className="w-3 h-3 mr-1" /> Notitie toevoegen
          </Button>
          {canConvertFin && (
            <Button
              onClick={onResendShopEmail}
              disabled={busy || !paid}
              variant="outline"
              className="rounded-none text-xs"
              data-testid="action-resend-shop-email"
            >
              <Mail className="w-3 h-3 mr-1" /> Bevestigingsmail opnieuw versturen
            </Button>
          )}
        </div>
        {!paid && (
          <div className="mt-3 text-xs text-amber-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Operationele acties zijn alleen beschikbaar voor betaalde bestellingen.
          </div>
        )}
      </div>

      {/* Conversies */}
      <div className="bg-white border border-gray-200 p-5">
        <h2 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-3">
          Documenten aanmaken vanuit bestelling
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {CONVERSIONS.map((c) => {
            const allowed = hasPermission(c.perm);
            const Icon = (DOC_TYPES[c.type] || {}).icon || FileText;
            const alreadyExists = docs.some((d) => d.type === c.type);
            return (
              <button
                key={c.type}
                onClick={() => onConvert(c, { force: false })}
                disabled={!allowed || busy}
                title={!allowed ? "Alleen Manager/Admin/Owner" : (alreadyExists ? "Bestaat al — opnieuw om te forceren" : c.label)}
                className={`group border-2 p-4 text-left transition-all ${
                  !allowed
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    : alreadyExists
                      ? "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                      : "border-gray-200 hover:border-[#0055FF] hover:bg-blue-50 text-[#0A192F]"
                }`}
                data-testid={`convert-${c.type}`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-5 h-5 mb-2" />
                  {!allowed && <Lock className="w-3 h-3" />}
                  {alreadyExists && allowed && (
                    <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-200 text-amber-900 px-1.5 py-0.5">
                      Bestaat
                    </span>
                  )}
                </div>
                <div className="font-chivo font-bold text-sm">{c.label}</div>
                <div className="text-[10px] uppercase tracking-wide opacity-70 mt-1">
                  {c.perm === "orders:convert_financial" ? "Manager+" : "Medewerker+"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gekoppelde documenten */}
      <div className="bg-white border border-gray-200 p-5" data-testid="linked-documents">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500">
            Gekoppelde documenten ({docs.filter((d) => !d.archived).length}{showArchived ? ` + ${docs.filter((d) => d.archived).length} gearchiveerd` : ""})
          </h2>
          {canConvertFin && (
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer" data-testid="toggle-archived-wrapper">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-3.5 h-3.5"
                data-testid="toggle-archived"
              />
              <EyeOff className="w-3 h-3" /> Toon gearchiveerde documenten
            </label>
          )}
        </div>
        {docs.length === 0 ? (
          <div className="text-sm text-gray-500">Nog geen documenten aangemaakt vanuit deze bestelling.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {docs.map((d, idx) => {
              const meta = DOC_TYPES[d.type] || { label: d.type, icon: FileText };
              const Icon = meta.icon;
              const isArchived = !!d.archived;
              const isDeleted = d.doc_exists === false;
              return (
                <div
                  key={`${d.type}-${d.id}-${idx}`}
                  className={`py-3 flex items-center gap-3 ${
                    isArchived ? "opacity-50 bg-gray-50 -mx-2 px-2" : ""
                  }`}
                  data-testid={`linked-doc-${d.type}-${idx}`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isArchived ? "text-gray-400" : "text-gray-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                      {meta.label}
                      {d.number && <span className="text-xs font-mono text-gray-500">{d.number}</span>}
                      {isArchived && (
                        <span className="inline-block text-[9px] px-1.5 py-0.5 bg-gray-200 text-gray-700 font-bold uppercase tracking-wide" data-testid={`linked-doc-${d.type}-${idx}-archived-badge`}>
                          Gearchiveerd
                        </span>
                      )}
                      {isDeleted && !isArchived && (
                        <span className="inline-block text-[9px] px-1.5 py-0.5 bg-red-100 text-red-700 font-bold uppercase tracking-wide" data-testid={`linked-doc-${d.type}-${idx}-deleted-badge`}>
                          Niet gevonden
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {d.created_at?.slice(0, 16).replace("T", " ")} · door {d.created_by_email || "-"}
                      {d.current_status && !isArchived && (
                        <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 font-bold uppercase">
                          {d.current_status}
                        </span>
                      )}
                      {isArchived && d.archive_reason && (
                        <div className="mt-1 italic">Reden: {d.archive_reason}</div>
                      )}
                      {isArchived && d.archived_by_email && (
                        <div>Gearchiveerd door {d.archived_by_email} · {d.archived_at?.slice(0, 16).replace("T", " ")}</div>
                      )}
                    </div>
                  </div>
                  {!isArchived && !isDeleted && meta.adminUrl && (
                    <a
                      href={meta.adminUrl(d.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#0055FF] hover:underline flex items-center gap-1"
                      data-testid={`linked-doc-open-${d.type}-${idx}`}
                    >
                      Openen <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {canConvertFin && !isArchived && (
                    <button
                      type="button"
                      onClick={() => setArchiveDialog({ doc: d, reason: "" })}
                      disabled={busy}
                      title={isDeleted ? "Koppeling opschonen — onderliggend document bestaat niet meer" : "Archiveer deze koppeling"}
                      className="text-xs text-gray-500 hover:text-amber-700 flex items-center gap-1"
                      data-testid={`linked-doc-archive-${d.type}-${idx}`}
                    >
                      <Archive className="w-3 h-3" />
                      {isDeleted ? "Opschonen" : "Archiveren"}
                    </button>
                  )}
                  {canConvertFin && isArchived && (
                    <button
                      type="button"
                      onClick={() => onUnarchiveDocument(d)}
                      disabled={busy}
                      className="text-xs text-gray-500 hover:text-[#0055FF] flex items-center gap-1"
                      data-testid={`linked-doc-unarchive-${d.type}-${idx}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Terugzetten
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order-info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 p-5">
          <h3 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-2">Klant</h3>
          <div className="text-sm">
            <div className="font-bold">{cust.name || "—"}</div>
            {cust.company && <div className="text-gray-600">{cust.company}</div>}
            <div className="text-gray-600">{cust.email || ""}</div>
            <div className="text-gray-600">{cust.phone || ""}</div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <h3 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-2">Bezorgadres</h3>
          <div className="text-sm text-gray-700">
            {ship.street ? (
              <>
                <div>{ship.street} {ship.house_number}{ship.house_number_addition || ""}</div>
                <div>{ship.postal_code || ship.postcode || ship.zip || ship.zip_code || ""} {ship.city}</div>
                <div>{ship.country}</div>
              </>
            ) : <div className="text-gray-500">— Geen bezorgadres —</div>}
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white border border-gray-200 p-5">
        <h3 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-3">Artikelen ({items.length})</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b">
              <th className="py-2 font-bold">Product</th>
              <th className="py-2 font-bold text-center w-16">Aantal</th>
              <th className="py-2 font-bold text-right w-24">Prijs</th>
              <th className="py-2 font-bold text-right w-28">Subtotaal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((it, idx) => {
              const qty = Number(it.quantity || 1);
              const unit = Number(it.unit_price || (it.unit_price_cents || 0) / 100);
              return (
                <tr key={idx}>
                  <td className="py-2">{it.product_name || it.description || "Artikel"}</td>
                  <td className="py-2 text-center">{qty}</td>
                  <td className="py-2 text-right">€ {unit.toFixed(2)}</td>
                  <td className="py-2 text-right font-bold">€ {(qty * unit).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Operationele notities */}
      {(order.operational_notes || []).length > 0 && (
        <div className="bg-white border border-gray-200 p-5">
          <h3 className="font-chivo font-bold uppercase tracking-[0.15em] text-xs text-gray-500 mb-3">Operationele notities</h3>
          <div className="space-y-3">
            {order.operational_notes.map((n, idx) => (
              <div key={n.id || idx} className="border-l-4 border-amber-300 bg-amber-50 p-3 text-sm" data-testid={`note-${idx}`}>
                <div className="text-gray-700">{n.text}</div>
                <div className="text-[10px] text-gray-500 mt-1">
                  {n.created_at?.slice(0, 16).replace("T", " ")} · {n.created_by_email}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notitie dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Operationele notitie toevoegen</DialogTitle>
            <DialogDescription>
              Deze notitie is intern. Maximaal 500 tekens.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Bijv. Klant belt vrijdag voor pickup om 14:00…"
            className="rounded-none min-h-[100px]"
            maxLength={500}
            data-testid="note-text-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)} className="rounded-none text-xs">
              Annuleren
            </Button>
            <Button
              onClick={onAddNote}
              className="rounded-none bg-[#0055FF] hover:bg-blue-700 text-white text-xs"
              disabled={!noteText.trim()}
              data-testid="note-submit"
            >
              Notitie opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate warning dialog */}
      <Dialog open={!!dupDialog} onOpenChange={(o) => !o && setDupDialog(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {dupDialog?.label} bestaat al
            </DialogTitle>
            <DialogDescription>
              Er is al een {dupDialog?.label?.toLowerCase()} gekoppeld aan deze
              bestelling. Wil je dit document nogmaals aanmaken? Dubbele documenten
              kunnen alleen door Manager/Admin/Owner worden geforceerd.
            </DialogDescription>
          </DialogHeader>
          {dupDialog?.existing && (
            <div className="text-xs bg-gray-50 border border-gray-200 p-3 font-mono">
              <div>Bestaand document: {dupDialog.existing.number || dupDialog.existing.id?.slice(0, 8)}</div>
              <div>Aangemaakt: {dupDialog.existing.created_at?.slice(0, 16).replace("T", " ")}</div>
              <div>Door: {dupDialog.existing.created_by_email || "-"}</div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDupDialog(null)}
              className="rounded-none text-xs"
              data-testid="dup-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={() => {
                const c = dupDialog;
                setDupDialog(null);
                onConvert(c, { force: true });
              }}
              disabled={!canConvertFin}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs"
              data-testid="dup-force"
            >
              {canConvertFin ? "Toch opnieuw aanmaken" : "Vereist Manager+"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation dialog */}
      <Dialog open={!!archiveDialog} onOpenChange={(o) => !o && setArchiveDialog(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Document archiveren
            </DialogTitle>
            <DialogDescription>
              Je verwijdert alleen de koppeling met deze bestelling. Het
              onderliggende document blijft bestaan en kan worden teruggezet
              via &quot;Toon gearchiveerde documenten&quot;.
            </DialogDescription>
          </DialogHeader>
          {archiveDialog?.doc && (
            <div className="text-xs bg-gray-50 border border-gray-200 p-3 font-mono space-y-1">
              <div>Type: {(DOC_TYPES[archiveDialog.doc.type] || {}).label || archiveDialog.doc.type}</div>
              {archiveDialog.doc.number && <div>Nummer: {archiveDialog.doc.number}</div>}
              <div>Aangemaakt: {archiveDialog.doc.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Reden (optioneel, max 500 tekens)
            </label>
            <Textarea
              value={archiveDialog?.reason || ""}
              onChange={(e) => setArchiveDialog((s) => s ? { ...s, reason: e.target.value } : s)}
              placeholder="Bijv. Verkeerde gegevens, klant heeft afgewezen, oude conceptfactuur…"
              className="rounded-none min-h-[70px]"
              maxLength={500}
              data-testid="archive-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveDialog(null)}
              className="rounded-none text-xs"
              data-testid="archive-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={() => onArchiveDocument(archiveDialog.doc, archiveDialog.reason)}
              disabled={busy}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs"
              data-testid="archive-confirm"
            >
              <Archive className="w-3 h-3 mr-1" /> Archiveer koppeling
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
